import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Clientes from '../components/Clientes';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Award, Globe, Users, Wrench, Clock, Factory, Fish, Utensils, Trees, TrendingUp, Users2, Headphones, Package, Target, Shield, Zap, Award as Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getOptimizedUrl, SERVICE_IMAGE_MAP, INDUSTRY_IMAGE_MAP } from '../generated-images-config';

const Home = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const handleQuoteNavigation = () => {
    navigate('/quote');
  };

  // Contador animado para estadísticas
  const useAnimatedCounter = (target: number, duration: number = 2000, suffix: string = '') => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (!isVisible) return;
      
      let startTime: number | null = null;
      const animateCount = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(easeOutQuart * target);
        
        setCount(currentCount);
        
        if (progress < 1) {
          requestAnimationFrame(animateCount);
        }
      };
      
      requestAnimationFrame(animateCount);
    }, [target, duration, isVisible]);
    
    return `${count}${suffix}`;
  };

  // Detectar cuando la sección es visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  // Estadísticas actualizadas
  const stats = [
    {
      icon: TrendingUp,
      number: useAnimatedCounter(15, 2000, '+'),
      label: 'Años de Experiencia',
      color: 'from-blue-400 to-cyan-300'
    },
    {
      icon: Users2,
      number: useAnimatedCounter(750, 2500, '+'),
      label: 'Clientes Satisfechos',
      color: 'from-purple-400 to-pink-300'
    },
    {
      icon: Shield,
      number: useAnimatedCounter(98, 1800, '%'),
      label: 'Tasa de Satisfacción',
      color: 'from-green-400 to-emerald-300'
    },
    {
      icon: Target,
      number: useAnimatedCounter(1200, 2200, '+'),
      label: 'Proyectos Completados',
      color: 'from-orange-400 to-red-300'
    },
    {
      icon: Zap,
      number: '24/7',
      label: 'Soporte Técnico',
      color: 'from-yellow-400 to-amber-300'
    },
    {
      icon: Package,
      number: useAnimatedCounter(15000, 3000, '+'),
      label: 'Productos Entregados',
      color: 'from-indigo-400 to-purple-300'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <Clientes />
        
        {/* Stats Section Mejorada */}
        <section className="stats-section relative" ref={statsRef}>
          {/* Partículas flotantes de fondo */}
          <div className="stats-particles">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  width: `${Math.random() * 4 + 2}px`,
                  height: `${Math.random() * 4 + 2}px`,
                  animationDelay: `${Math.random() * 15}s`,
                  animationDuration: `${Math.random() * 10 + 15}s`
                }}
              />
            ))}
          </div>
          
          <div className="container-custom stats-container">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-sm font-medium mb-6 border border-white/20">
                <Trophy className="w-4 h-4 mr-2" />
                Nuestros Logros
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Cifras que
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-100">
                  Inspiran Confianza
                </span>
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Más de una década de compromiso inquebrantable con la excelencia industrial,
                respaldado por resultados que hablan por sí mismos.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div
                    key={index}
                    className="stat-card animate-count"
                    style={{
                      animationDelay: `${index * 0.1}s`,
                      background: `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)`
                    }}
                  >
                    <IconComponent className="stat-icon" />
                    <div className={`stat-number ${isVisible ? 'pulse' : ''}`}>
                      {stat.number}
                    </div>
                    <div className="stat-label">{stat.label}</div>
                    
                    {/* Efecto de brillo adicional */}
                    <div
                      className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                      style={{
                        background: `linear-gradient(135deg, ${stat.color.replace('from-', 'rgba(255, 255, 255, 0.1)').replace(' to-', ', rgba(255, 255, 255, 0.05)')})`
                      }}
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Mensaje adicional */}
            <div className="text-center mt-12">
              <p className="text-white/80 text-lg italic">
                "Cada número representa una historia de éxito y un compromiso renovado con nuestros clientes"
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
                ¿Por qué elegirnos?
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Tu Socio Estratégico en
                <span className="text-gradient"> Soluciones Industriales</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Combinnos experiencia técnica, calidad excepcional y un compromiso inquebrantable
                con el éxito de nuestros clientes.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <Award className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Calidad Certificada</h3>
                <p className="text-gray-600">
                  Todos nuestros productos y servicios cumplen con los más altos estándares
                  internacionales de calidad y seguridad.
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <Globe className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Alcance Nacional</h3>
                <p className="text-gray-600">
                  Presencia en todo el Perú con capacidad de despacho internacional
                  y soporte técnico en cualquier ubicación.
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Equipo Experto</h3>
                <p className="text-gray-600">
                  Ingenieros y técnicos especializados con más de 15 años de experiencia
                  en diversas industrias.
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <Wrench className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Soluciones Integrales</h3>
                <p className="text-gray-600">
                  Desde el diagnóstico inicial hasta la implementación completa,
                  ofrecemos soluciones de extremo a extremo.
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Respuesta Rápida</h3>
                <p className="text-gray-600">
                  Tiempos de respuesta garantizados y atención de emergencia
                  las 24 horas del día, los 7 días de la semana.
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <Award className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Garantía Total</h3>
                <p className="text-gray-600">
                  Respaldamos todos nuestros trabajos con garantías completas
                  y seguimiento post-venta continuo.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
                Nuestros Servicios
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Soluciones
                <span className="text-gradient"> Industriales</span>
                <span className="text-gradient"> Complejas</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Ofrecemos un portafolio integral de servicios diseñados para maximizar
                la eficiencia y confiabilidad de sus operaciones.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                {/* Imagen del servicio */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={getOptimizedUrl(SERVICE_IMAGE_MAP.maintenance, 400, 200)}
                    alt="Mantenimiento Industrial"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <Wrench className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Mantenimiento Industrial</h3>
                  <p className="text-gray-600 mb-4">
                    Servicio completo de mantenimiento preventivo y correctivo para maquinaria industrial.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      Mantenimiento preventivo
                    </li>
                    <li className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      Reparaciones de emergencia
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={handleQuoteNavigation}>
                    Cotizar Servicio
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                {/* Imagen del servicio */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={getOptimizedUrl(SERVICE_IMAGE_MAP.installation, 400, 200)}
                    alt="Instalación y Montaje"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Instalación y Montaje</h3>
                  <p className="text-gray-600 mb-4">
                    Instalación profesional de equipos industriales con garantía de cumplimiento de normativas.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      Montaje de maquinaria
                    </li>
                    <li className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      Puesta en marcha
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={handleQuoteNavigation}>
                    Cotizar Servicio
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                {/* Imagen del servicio */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={getOptimizedUrl(SERVICE_IMAGE_MAP.consulting, 400, 200)}
                    alt="Consultoría Técnica"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Consultoría Técnica</h3>
                  <p className="text-gray-600 mb-4">
                    Asesoría especializada para optimización de procesos y selección de equipos.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      Auditoría de procesos
                    </li>
                    <li className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      Ingeniería de proyectos
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={handleQuoteNavigation}>
                    Cotizar Servicio
                  </Button>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button className="btn-primary px-8 py-4 text-lg" onClick={() => navigate('/services')}>
                Ver Todos los Servicios
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Featured Industries */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-accent-100 text-accent-700 rounded-full text-sm font-medium mb-6">
                Industrias que Servimos
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expertos en
                <span className="text-gradient"> Múltiples Sectores</span>
                <span className="text-gradient"> Industriales</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Contamos con experiencia comprobada en diversas industrias,
                ofreciendo soluciones especializadas para cada sector.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* Imagen de la industria */}
                <div className="h-40 overflow-hidden">
                  <img
                    src={getOptimizedUrl(INDUSTRY_IMAGE_MAP.paper, 400, 200)}
                    alt="Industria Papelera"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Factory className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Industria Papelera</h3>
                  <p className="text-gray-600 text-sm">Equipos y servicios especializados para producción de papel</p>
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* Imagen de la industria */}
                <div className="h-40 overflow-hidden">
                  <img
                    src={getOptimizedUrl(INDUSTRY_IMAGE_MAP.fishing, 400, 200)}
                    alt="Industria Pesquera"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Fish className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Industria Pesquera</h3>
                  <p className="text-gray-600 text-sm">Soluciones para procesamiento y conservación</p>
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* Imagen de la industria */}
                <div className="h-40 overflow-hidden">
                  <img
                    src={getOptimizedUrl(INDUSTRY_IMAGE_MAP.food, 400, 200)}
                    alt="Industria Alimentaria"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Utensils className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Industria Alimentaria</h3>
                  <p className="text-gray-600 text-sm">Equipos certificados para alimentos y bebidas</p>
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* Imagen de la industria */}
                <div className="h-40 overflow-hidden">
                  <img
                    src={getOptimizedUrl(INDUSTRY_IMAGE_MAP.agri, 400, 200)}
                    alt="Agroindustria"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-yellow-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Trees className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Agroindustria</h3>
                  <p className="text-gray-600 text-sm">Maquinaria para procesamiento agrícola</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button className="btn-primary px-8 py-4 text-lg" onClick={() => navigate('/industries')}>
                Explorar Todas las Industrias
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary-600 to-accent-500">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Listo para Optimizar tus Operaciones Industriales?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Nuestro equipo de expertos está listo para analizar tus necesidades
              y desarrollar soluciones a medida para tu industria.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 text-lg" onClick={handleQuoteNavigation}>
                Solicitar Cotización
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button variant="outline" className="border-white bg-white/10 text-white hover:bg-white hover:text-primary-600 px-8 py-4 text-lg backdrop-blur-sm" onClick={() => navigate('/contact')}>
                Contactar Asesor
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;